import classnames from "classnames";
import { inject, observer } from "mobx-react";
import React, { Component } from "react";
import type { Store } from "../../../../client/stores";
import "../../../core/chrome/UserProfile.style.scss";
import { ExpandIcon } from "../../../core/components/icons/ExpandIcon.component";
import { UserIcon } from "../../../core/components/icons/UserIcon.component";
import type { SessionState } from "../../../core/lib/auth/backends";
import { isPWSSessionBackend } from "../../../core/lib/auth/backends/pws";
import { placeholderImage } from "../../../core/lib/image";
import { Button, ButtonKind, FauxButton } from "../../../core/ui";

interface CoastUserProfileProps {
    store?: Store;
}

interface CoastUserProfileState {
    loggedIn: boolean;
}

@inject("store")
@observer
export class CoastUserProfile extends Component<CoastUserProfileProps, CoastUserProfileState> {
    mounted = false;

    constructor(props: CoastUserProfileProps) {
        super(props);

        this.state = { loggedIn: false };
    }

    private update(state: SessionState) {
        if (this.mounted) {
            this.setState({
                loggedIn: state && state.authenticated && !!state.profile,
            });
        }
    }

    login = () => {
        if (this.props.store) {
            this.props.store.onAuthAction.dispatch({
                action: "pending",
            });

            this.props.store.session?.currentSession?.login();
        }
    };

    logout = () => {
        this.props.store?.session?.currentSession?.logout();
    };

    componentDidMount() {
        this.mounted = true;

        if (this.props.store) {
            const { session } = this.props.store;

            if (session?.currentSession) {
                this.update(session.currentSession.state);

                session.currentSession.onStatusChanged.subscribe(state => {
                    if (this.props.store) {
                        this.props.store.onAuthAction.dispatch({
                            action: "resolve",
                        });
                    }

                    this.update(state);
                });
            }
        }
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    getProfilePic = () => {
        const session = this.props.store?.session.currentSession;

        if (this.state.loggedIn && isPWSSessionBackend(session)) {
            const { profile } = session.state;

            if (profile) {
                return (
                    <Button
                        click={() => session.showUsersPopup("account")}
                        kind={ButtonKind.CUSTOMIZED}
                        className="settings"
                    >
                        <img
                            className="profile-pic lazyload"
                            alt="Profile Image"
                            data-src="https://i.iheart.com/v3/re/new_assets/001f1ff9-3d33-4d63-9cd5-c63b30cdd733?ops=fit(48,48),quality(80)"
                            src={placeholderImage}
                        />
                    </Button>
                );
            }

            return null;
        }

        return (
            <Button kind={ButtonKind.CUSTOMIZED} click={this.login} label="login">
                <UserIcon />
            </Button>
        );
    };

    getSettingsButton = () => {
        const session = this.props.store?.session.currentSession;

        if (!isPWSSessionBackend(session)) {
            return null;
        }

        return (
            <Button click={() => session.showUsersPopup("account")} kind={ButtonKind.CUSTOMIZED} className="settings">
                My Account
            </Button>
        );
    };

    getUserOptions = () => {
        if (this.state.loggedIn) {
            return (
                <React.Fragment>
                    {this.getSettingsButton()}
                    <Button kind={ButtonKind.CUSTOMIZED} className="logout" click={this.logout}>
                        Logout
                    </Button>
                </React.Fragment>
            );
        }

        return (
            <React.Fragment>
                <Button kind={ButtonKind.CUSTOMIZED} className="login" click={this.login}>
                    Login
                </Button>
                <FauxButton kind={ButtonKind.CUSTOMIZED} className="signup" to="/sign-up/">
                    Signup
                </FauxButton>
            </React.Fragment>
        );
    };

    render() {
        if (this.props.store) {
            const { session } = this.props.store;
            let profile = null;
            let username = null;

            if (session.currentSession) {
                profile = session.currentSession.state.profile;
                username = profile ? (profile.name || profile.email || "").split("@")[0] : null;
            }

            const userClass = classnames("user-account", {
                authenticated: !!session.currentSession?.authenticated,
            });

            const ProfilePic = () => this.getProfilePic();
            const UserOptions = () => this.getUserOptions();

            return (
                <section className={userClass}>
                    <figure className="user-info">
                        <section className="thumb">
                            <ProfilePic />
                        </section>
                        <i className="menu-toggler">
                            <ExpandIcon />
                        </i>
                        {username ? <figcaption className="username">{username}</figcaption> : null}
                    </figure>
                    <div className="options">
                        <UserOptions />
                    </div>
                </section>
            );
        }

        return null;
    }
}

export default CoastUserProfile;
